import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged, User as FirebaseUser, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { hasEnrolledTotp } from '../lib/nativeMfa';

interface AdminUser extends FirebaseUser {
  role: string;
}

interface AdminContextType {
  user: AdminUser | null;
  isFounder: boolean;
  /**
   * Whether the signed-in founder has a native GCIP TOTP factor enrolled.
   * With native MFA the second factor is challenged at sign-in, so a signed-in
   * founder with an enrolled factor is verified BY CONSTRUCTION — there is no
   * separate "verified this session" step and no `sessionStorage` gate. A
   * founder WITHOUT a factor is routed to /mfa/setup by AdminRouteGuard.
   */
  mfaEnrolled: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isFounder, setIsFounder] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleSignOut = useCallback(async () => {
    setMfaEnrolled(false);
    await signOut(auth);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // SECURITY: Validate user has a valid profile in Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

          if (!userDoc.exists()) {
            console.error('[AdminContext] SECURITY: User has no profile in Firestore, signing out');
            await handleSignOut();
            setUser(null);
            setIsFounder(false);
            setMfaEnrolled(false);
            setLoading(false);
            return;
          }

          const userData = userDoc.data();

          // SECURITY: Check if user status is active
          const userStatus = userData.status || 'active';
          if (userStatus === 'revoked' || userStatus === 'suspended') {
            console.error('[AdminContext] SECURITY: User status is revoked/suspended:', userStatus);
            await handleSignOut();
            setUser(null);
            setIsFounder(false);
            setMfaEnrolled(false);
            setLoading(false);
            return;
          }

          const idTokenResult = await firebaseUser.getIdTokenResult(true);
          const claims = idTokenResult.claims as { role?: string; isFounder?: boolean };
          const adminRoles = ['founder'];
          const userRole = userData.role || claims.role || '';

          // SECURITY: Only founders can access the admin portal
          if (!adminRoles.includes(userRole)) {
            console.warn(`[AdminContext] SECURITY: User '${firebaseUser.email}' (role: ${userRole}) is not an authorized admin. Signing out.`);
            await handleSignOut();
            setUser(null);
            setIsFounder(false);
            setMfaEnrolled(false);
            setLoading(false);
            return;
          }

          // Native GCIP: the second factor is challenged at sign-in. A founder
          // who reached an authenticated session with an enrolled factor has, by
          // construction, already passed the challenge. A founder WITHOUT a
          // factor is routed to /mfa/setup by AdminRouteGuard to enrol. No
          // sessionStorage gate, no bespoke `verifyLoginMFA` callable.
          setUser({ ...firebaseUser, role: userRole });
          setIsFounder(true);
          setMfaEnrolled(hasEnrolledTotp(firebaseUser));
        } catch (error) {
          console.error('[AdminContext] Error verifying admin access:', error);
          await handleSignOut();
          setUser(null);
          setIsFounder(false);
          setMfaEnrolled(false);
        } finally {
          setLoading(false);
        }
      } else {
        setMfaEnrolled(false);
        setUser(null);
        setIsFounder(false);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [handleSignOut]);

  // Session timeout: 30 min idle → auto-logout, 5 min warning
  const handleSessionTimeout = useCallback(async () => {
    await signOut(auth).catch(() => {});
    window.location.href = '/login?expired=true';
  }, []);

  const { showWarning, remainingSeconds, extendSession } = useSessionTimeout({
    timeoutMs: 30 * 60 * 1000,   // 30 minutes
    warningMs: 5 * 60 * 1000,     // 5 min warning
    onTimeout: handleSessionTimeout,
    enabled: isFounder && mfaEnrolled,
  });

  return (
    <AdminContext.Provider value={{ user, isFounder, mfaEnrolled, loading, signOut: handleSignOut }}>
      {children}
      {showWarning && (
        <SessionTimeoutWarning
          remainingSeconds={remainingSeconds}
          onExtend={extendSession}
          onSignOut={handleSignOut}
        />
      )}
    </AdminContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
